// nebula/march.js: per-frame volume shaders. MEDIUM (shared medium functions),
// MARCH (low-res raymarch of the baked volume + analytic protostar), VIS (1x1 star visibility).
import { COMMON } from './glsl.js';

export const MEDIUM = /* glsl */`
uniform sampler3D uVol, uLit, uDet;
uniform vec3 uBoxMin, uBoxMax, uBoxInv;
uniform float uTime;
uniform vec3 uStar, uAxis, uB1, uB2;
uniform vec4 uMed;      // dust scale, erosion, detail frequency, dust extinction
uniform vec4 uGasP;     // gas scale, gas extinction, filament contrast, -
uniform vec3 uRedden;   // extinction per channel (blue absorbed more)
uniform vec4 uCoc;      // radius, density, swirl, collapse 0..1
uniform vec4 uHeat;     // radius, intensity, temperature 0..1, heating glow on the nebula
uniform vec4 uDisk;     // amount, radius, brightness, flare
uniform vec4 uShellB;   // radius, thickness, density, glow
uniform vec4 uPull;     // gravity: radial pull, swirl angle, radius, blown cavity radius
uniform vec4 uStir; uniform vec3 uStirO; uniform vec2 uFloorF;
uniform vec3 uSAx, uSB1, uSB2;   // infall-streamer frame: tilted toward the camera so the in-spiral reads
uniform vec3 uLDir, uLCol, uAlb, uAlbL;
uniform vec3 uHeatA, uHeatB;
// gravity: sample the cloud further out and rotated about the axis, so its features are
// drawn in toward the core and wound into a spiral as the visitor holds
vec3 pullP(vec3 p) {
  if (uPull.x == 0.0 && uPull.y == 0.0) return p;
  vec3 q = p - uStar; float r = length(q);
  float k = exp(-r / uPull.z);
  float a = uPull.y * k * k;
  float h = dot(q, uAxis); vec3 qr = q - uAxis * h;
  q = uAxis * h + qr * cos(a) + cross(uAxis, qr) * sin(a);
  return uStar + q * (1.0 + uPull.x * k);
}
// collapsing protostellar cocoon: analytic, stateless flow. Two advection phases are
// cross-faded so the differential rotation never winds the texture up.
float cocoon(vec3 q, float r, vec3 rd, out vec3 S) {
  S = vec3(0.0);
  float R = uCoc.x;
  float h = dot(q, uAxis); vec3 qr = q - uAxis * h; float rc = length(qr);
  float coll = uCoc.w;
  float fl = 1.0 + 2.2 * coll;
  float env = exp(-(rc * rc + sq(h * fl)) / (R * R));
  float ph = atan(dot(qr, uB2), dot(qr, uB1));
  const float PER = 7.0;
  float tA = fract(uTime / PER), tB = fract(uTime / PER + 0.5);
  float wA = 1.0 - abs(2.0 * tA - 1.0);
  float om = uCoc.z / (pow(rc + 0.06, 1.5) + 0.15);
  float vin = 0.08 + 0.35 * coll;
  float aA = om * tA * 1.1, aB = om * tB * 1.1;
  float gA = exp(vin * tA * 1.1), gB = exp(vin * tB * 1.1);
  vec3 pA = vec3(cos(ph - aA) * rc * gA, sin(ph - aA) * rc * gA, h * gA);
  vec3 pB = vec3(cos(ph - aB) * rc * gB, sin(ph - aB) * rc * gB, h * gB);
  vec4 nA = textureLod(uDet, pA * 0.5 + vec3(0.13, 0.71, 0.37), 0.0);
  vec4 nB = textureLod(uDet, pB * 0.5 + vec3(0.61, 0.29, 0.83), 0.0);
  vec4 nn = mix(nB, nA, wA);
  float n = nn.r;
  // streamers (like the observed infall streamers onto real protostars) need not lie in the disk
  // plane: their own frame faces the camera, so the arcs read as a spiral instead of a flat line
  float hS = dot(q, uSAx); vec3 qS = q - uSAx * hS; float rcS = length(qS);
  float phS = atan(dot(qS, uSB2), dot(qS, uSB1));
  float lr = log(max(rcS, 0.03));
  float arm = 0.5 + 0.5 * cos(2.0 * (phS + lr * 2.4) + uTime * 0.35 * uCoc.z + nn.a * 1.2);
  float armS = arm * arm * arm * arm, armN = armS * armS;
  float armK = smoothstep(0.1, 0.6, coll);
  float cav = smoothstep(0.64, 0.88, abs(h) / max(r, 1e-3)) * smoothstep(0.3, 0.8, coll);
  float d = env * (0.1 + 0.6 * n * n) * mix(1.0, 0.15 + 1.5 * armS, armK * 0.9);
  float Hs = 0.05 + 0.14 * rcS;
  float strm = smoothstep(0.02, 0.35, coll) * armN * exp(-sq(hS / Hs)) * exp(-sq(rcS / (2.0 * R))) * (1.0 - smoothstep(1.5 * R, 2.15 * R, r)) * smoothstep(0.2, 0.7, n + 0.3 * nn.b);
  d += 1.6 * strm;
  d *= 1.0 - 0.94 * cav;
  d *= smoothstep(0.03, 0.12, r);
  d *= uCoc.y;
  // protostar light: escapes along the outflow cavities, blocked near the midplane by the disk
  float tau = uCoc.y * uMed.w * R * 0.3 * tanh(1.4 * r / R) * (1.0 - 0.92 * cav);
  tau += uDisk.x * 7.0 * exp(-sq(h / (0.04 + 0.3 * rc))) * smoothstep(0.03, 0.15, rc);
  float Eh = uHeat.y * exp(-tau * 0.5) / (1.0 + sq(r / uHeat.x));
  vec3 hc = mix(uHeatA, uHeatB, clamp(uHeat.z * (1.2 - 0.7 * r / R), 0.0, 1.0));
  // streamers glow on their own (shock-heated infall), so the spiral reads against the dark
  float wall = smoothstep(0.42, 0.66, abs(h) / max(r, 1e-3)) * smoothstep(0.2, 0.7, coll);
  float Ew = uHeat.y * 0.6 * wall / (1.0 + sq(r / (0.7 * R)));
  vec3 wc = mix(uHeatB, vec3(0.62, 0.72, 1.0), smoothstep(0.25 * R, 1.1 * R, r));
  S = Eh * hc + Ew * wc * 1.6 + strm * uHeat.y * 1.15 * mix(uHeatA, uHeatB, 0.3) / (1.0 + sq(r / (R * 0.75)));
  float rim = smoothstep(0.1, 0.9, dot(q / max(r, 1e-3), uLDir)) * smoothstep(0.35 * R, 1.2 * R, r);
  S += uLCol * uAlbL * rim * hg(dot(rd, uLDir), 0.4) * 0.12;
  return d;
}
float diskD(vec3 q, out vec3 S) {
  S = vec3(0.0);
  float h = dot(q, uAxis); float rc = length(q - uAxis * h);
  if (rc > uDisk.y * 1.3) return 0.0;
  float H = 0.012 + uDisk.w * rc;
  float sig = smoothstep(0.02, 0.06, rc) * sst(uDisk.y, uDisk.y * 0.45, rc);
  float d = uDisk.x * sig * exp(-sq(h / H)) * 22.0;
  float surf = smoothstep(0.5, 1.7, abs(h) / H);
  vec3 hc = mix(uHeatA, uHeatB, 0.95);
  S = uDisk.z * hc * (surf * 1.4 / (1.0 + sq(rc / 0.07)) + 2.0 * exp(-rc / 0.03));
  return d;
}
float shellD(vec3 q, float r) {
  float x = (r - uShellB.x) / uShellB.y;
  if (abs(x) > 3.0) return 0.0;
  float n = textureLod(uDet, q * 0.3 + vec3(0.5, 0.2, 0.9), 0.0).r;
  return exp(-x * x) * uShellB.z * (0.2 + 1.6 * n * n);
}
vec3 stir(vec3 p) {
  if (uStir.w <= 0.0) return p;
  vec3 o = p - uStirO; float a = dot(o, uStir.xyz); vec3 perp = o - uStir.xyz * a;
  float d = length(perp);
  float k = uStir.w * exp(-sq(d / 0.8));
  return p + cross(uStir.xyz, perp) * k * 0.7 - perp * k * 0.12;
}
`;

/* ---------------- per-frame raymarch ---------------- */
export const MARCH = /* glsl */`
precision highp float; precision highp sampler3D;
uniform vec3 uCamPos; uniform mat3 uCamMat; uniform vec2 uTan, uShift;
uniform float uPxA, uDetN, uStepK;
uniform vec4 uRHz;      // ring cavity haze (closed form): density, radius; z: base erosion billow(0)..ridged(1) mix
uniform vec4 uStr;      // striations: erosion, shading mix, frequency, radial mode (= ring radius; 0 = along the cluster light)
uniform vec4 uLightP;   // star luminosity, falloff radius, light-front radius, front width
uniform float uBand;    // brightness of the light front (ignition shock)
uniform vec3 uStarCol;
uniform vec3 uCO, uCH, uCS, uCR, uCR2, uAmb, uCG;
uniform vec4 uRim;      // cluster-front glow, star-front glow
uniform vec4 uGain;     // gas emission, rim emission, scattering, ionisation scale
uniform vec4 uGasF;     // front gas amount, cluster gas glow, cavity radius, tap strength
uniform vec4 uJet;      // amount, length, brightness, width
uniform vec3 uShellCol;
uniform vec3 uBgA, uBgB; uniform float uBgI; uniform vec4 uHz;   // horizon glow: amount, y, falloff, -
uniform float uLI, uSW; // cluster light intensity; shadow-tap direction weight (0 cluster .. 1 star)
uniform vec3 uDustE;    // dust thermal/PAH glow (so shadowed dust reads rust-brown, not black)
uniform vec4 uFill;     // diffuse fill (UV-excited dust glow): intensity, y low, y high, -
uniform vec3 uFillC;
uniform vec4 uAlbC;     // r7: cavity-wall albedo (rgb) + amount near the star (w)
uniform float uNearK;   // r7: after ignition the near-star step refinement relaxes (cavity is dust-free; saves steps)
uniform vec4 uCavD;     // r7: unit vector star -> camera, squash: the blown cavity opens toward the viewer
#if R8
uniform vec4 uR8, uR8b, uR8c, uVeilY; uniform vec3 uVeilC;   // r8 Cosmic Cliffs: compiled only into the lit-ignite program (R8=1, world.js)
#endif
#if R9
uniform vec4 uR9;   // r9 (R9=1 lit program only): x pre-integrated skin fringe (k_pi), y star-skin gain, z dome breakup, w thin dome wall
#endif
uniform vec4 uDep;      // r7 depth: gas/glow off in star-shadowed dust, low-ion gas dim, bulk diffuse dim; w: striation modulation
varying vec2 vUv;
${COMMON}
${MEDIUM}
#if R9
// r9: the emitting dust fringe (rise over dust 0..0.12, fall over 0.06..0.28) and its antiderivative, for the per-step
// pre-integration of the skins (ramp difference; rI = integral of clamp((d - a) / (b - a), 0, 1))
float rI(float d, float a, float b) { float x = clamp((d - a) / (b - a), 0.0, 1.0); return 0.5 * (b - a) * x * x + max(d - b, 0.0); }
float frB(float d) { return clamp(d / 0.12, 0.0, 1.0) - clamp((d - 0.06) / 0.22, 0.0, 1.0); }
float frI(float d) { return rI(d, 0.0, 0.12) - rI(d, 0.06, 0.28); }
#endif
vec3 jetGlow(vec3 ro, vec3 rd) {
  if (uJet.x <= 0.0) return vec3(0.0);
  vec3 a = uAxis; vec3 w0 = ro - uStar;
  float b = dot(rd, a), d = dot(rd, w0), e = dot(a, w0);
  float den = max(1.0 - b * b, 1e-4);
  float s = clamp((e - b * d) / den, -uJet.y, uJet.y);
  vec3 pa = uStar + a * s;
  float tr = max(dot(pa - ro, rd), 0.0);
  float dist = length(ro + rd * tr - pa);
  float as = abs(s) / uJet.y;
  float w = uJet.w * (0.4 + 1.8 * as);
  float prof = exp(-sq(dist / w)) * uJet.w / (w * sqrt(den) + 0.2 * uJet.w);
  float side = s < 0.0 ? 0.37 : 0.0;
  float k = 0.0;
  for (int i = 0; i < 4; i++) {
    float ph = fract(uTime * 0.11 + float(i) * 0.25 + side);
    k += exp(-sq((as - ph) / 0.03)) * (1.0 - ph * 0.5);
  }
  float basev = exp(-as * 5.0);
  float tn = textureLod(uDet, vec3(abs(s) * 0.45 - uTime * 0.06 + side, dist * 1.3, 0.37 + side), 0.0).r;
  prof *= 0.45 + 1.1 * tn;
  float I = (basev * 2.6 + k * 1.6 + 0.22) * sst(1.0, 0.8, as);
  float bow = exp(-sq(dist / (w * 1.6))) * exp(-sq((as - 0.95 + 0.6 * sq(dist / uJet.y)) / 0.035)) * 0.8 * tn;
  vec3 c = mix(vec3(1.0, 0.18, 0.34), vec3(1.0, 0.8, 0.7), basev);
  return uJet.x * uJet.z * (I * prof + bow) * c;
}
vec3 hazeGlow(vec3 ro, vec3 rd) {
  // M57: faint OIII haze filling the ring's cavity, closed-form (no march cost); bluer at the centre
  if (uRHz.x <= 0.0) return vec3(0.0);
  vec3 w = uStar - ro; float s = dot(w, rd); float b2 = max(dot(w, w) - s * s, 0.0);
  float g = exp(-b2 / sq(uRHz.y));
  float fall = uLightP.x / (1.0 + sq(uRHz.y * 0.6 / uLightP.y)) * smoothstep(0.0, uRHz.y, uLightP.z);
  vec3 c = mix(uCO, vec3(0.45, 0.8, 1.0), 0.45 * g * g) * g;
  if (uRHz.w > 0.0) {
    // ignite (r7): the newborn star's blown ionised cavity, lit from inside: teal-turquoise core and a
    // limb-brightened magenta shell where the gas meets the cavity walls; ragged by one noise fetch per ray
    vec3 pb = (ro + rd * s - uStar) / uRHz.y;
    vec4 hn = textureLod(uDet, pb * 0.7 + vec3(0.17, 0.53, 0.29), 0.0);
    float b = length(pb);
    float wall = exp(-sq((b - 0.9 - 0.4 * (hn.r - 0.5)) / (0.2 + 0.25 * hn.g)));
#if R9
    // r9: break up the smooth slate dome: ridged turquoise wisps over deep teal (one more fetch per ray), thin bright wall
    float fil = smoothstep(0.35, 0.8, textureLod(uDet, pb * 2.3 + hn.gba * 0.35 + vec3(0.41, 0.07, 0.66), 1.5).b);
    vec3 tc = mix(vec3(0.03, 0.55, 0.62), mix(vec3(0.02, 0.4, 0.5), vec3(0.05, 0.66, 0.68), fil), uR9.z) * mix(1.0, 0.5 + 1.2 * fil, uR9.z);
    float wl9 = exp(-sq((b - 0.9 - 0.4 * (hn.r - 0.5)) / mix(0.2 + 0.25 * hn.g, 0.07 + 0.1 * hn.g, uR9.w)));
    c = tc * g * (0.55 + 0.9 * hn.a) * uR8c.y + uCH * wl9 * uRHz.w * (0.35 + 1.3 * hn.b * hn.b) * mix(1.0, 1.9 * (0.3 + 1.2 * fil), uR9.w);
#elif R8
    c = vec3(0.03, 0.55, 0.62) * g * (0.55 + 0.9 * hn.a) * uR8c.y + uCH * wall * uRHz.w * (0.35 + 1.3 * hn.b * hn.b);
#else
    c = vec3(0.03, 0.55, 0.62) * g * (0.55 + 0.9 * hn.a) + uCH * wall * uRHz.w * (0.35 + 1.3 * hn.b * hn.b);
#endif
  }
  return uRHz.x * 1.7 * uRHz.y * fall * uGain.x * c;
}
vec3 backdrop(vec3 rd) {
  vec4 a = texture(uDet, rd * 0.33 + vec3(0.31, 0.47, 0.11));
  vec4 b = texture(uDet, rd * 0.85 + vec3(0.7, 0.2, 0.5));
  float neb = smoothstep(0.3, 0.9, a.w * 0.7 + b.r * 0.3) * (0.5 + 0.9 * b.b);
  float glow = pow(max(dot(rd, uLDir), 0.0), 3.0);
  // the ionised cavity above the ridge: brightest hugging the cliff line, streaked by the noise
  float hz = exp(-max(rd.y - uHz.y, 0.0) / uHz.z) * uHz.x * smoothstep(uHz.y - 0.1, uHz.y + 0.02, rd.y);
  vec3 hc = mix(uBgB, vec3(0.35, 0.6, 1.0), 0.5 * hz) * (0.45 + 1.1 * a.w * (0.5 + b.b));
  float stk = smoothstep(0.5, 0.85, texture(uDet, vec3(rd.x * 1.6, rd.y * 0.22, rd.z * 1.6) + 0.43).b) * smoothstep(-0.05, 0.25, rd.y);
  return uBgI * (mix(uBgA, uBgB, a.r) * neb + uBgB * glow * (0.6 + 0.8 * a.w) + hc * hz * 1.6 + uBgB * stk * 0.35) * (0.45 + 0.55 * exp(-max(rd.y - 0.1, 0.0) * 2.5));
}
void main() {
  vec2 ndc = vUv * 2.0 - 1.0 - uShift * 2.0;
  vec3 rd = normalize(uCamMat * vec3(ndc * uTan, -1.0));
  vec3 ro = uCamPos;
  vec2 tb = boxHit(ro, rd, uBoxMin, uBoxMax);
  float t0 = max(tb.x, 0.0), t1 = tb.y;
  vec3 col = vec3(0.0), T = vec3(1.0);
  float tc = dot(uStar - ro, rd);
  bool jetDone = false;
  float jit = fract(52.9829189 * fract(dot(gl_FragCoord.xy, vec2(0.06711056, 0.00583715))));
  vec3 drift = vec3(0.0, uTime * 0.0018, uTime * 0.0011);
  vec3 ldT = uLDir;
  if (t1 > t0) {
    float maxI = min(float(STEPS), floor(1.45 / uStepK + 0.5));   // backdrop / tiers: fewer steps via uStepK
    float dtFloor = max((t1 - t0) / maxI * 0.3, 0.01);
    float t = t0;
    float rs0 = length(ro + rd * t - uStar);
    t += clamp(min(uStepK * max(t, 1.0), 0.12 * max(rs0, uNearK) + 0.012), dtFloor, 0.5) * jit;
    float sigPrev = 1e-3, dtL = 0.0;
#if R9
    float dPrev = 0.0, clPrev = 1.0;   // r9: previous step's dust + star-tap clear (skin pre-integration)
#endif
    for (int i = 0; i < STEPS; i++) {
      if (float(i) >= maxI || t > t1 || max(T.r, max(T.g, T.b)) < 0.025) break;
      // per-step sample offset (golden-ratio sequence): turns step contours (wood grain) into fine noise
      float wn = fract(52.9829189 * fract(dot(gl_FragCoord.xy + float(i) * 5.588238, vec2(0.06711056, 0.00583715))));
      vec3 p = ro + rd * (t + dtL * (wn - 0.5) * 0.6);
      vec3 q = p - uStar; float r2 = dot(q, q); float rs = sqrt(r2);
      float dt = clamp(min(uStepK * max(t, 1.0), 0.12 * max(rs, uNearK) + 0.012), dtFloor, 0.5);
      if (!jetDone && t > tc) { jetDone = true; col += T * (jetGlow(ro, rd) + hazeGlow(ro, rd)); }
      vec3 ps = pullP(stir(p));
      vec3 uvw = (ps - uBoxMin) * uBoxInv;
      vec4 m = texture(uVol, uvw);
      float comp = 1.0 + 0.7 * uPull.x * exp(-rs / uPull.z);
      float hcv = max(dot(q, uCavD.xyz), 0.0);
      float cav = uGasF.z > 0.0 ? smoothstep(uGasF.z * 0.5, uGasF.z * 1.3, sqrt(max(r2 - uCavD.w * hcv * hcv, 0.0))) : 1.0;
      float dustM = m.r * m.r * comp * cav, gasM = m.g * m.g, frM = m.a * m.a;
      if (uFloorF.y > 0.0) { float fz = smoothstep(0.0, uFloorF.y, uvw.y); dustM *= fz; gasM *= fz; frM *= fz; }
      float nearS = uAlbC.w * exp(-r2 / sq(uGasF.z * 1.5 + 0.2));   // r7: the star's cavity walls
      float dust = 0.0, gas = 0.0, ridge = 0.5, lod = 0.0, n1r = 0.5, strM = 1.0;
      vec3 dp = vec3(0.0);
      vec4 lk = vec4(1.0, 1.0, 0.0, 0.0);
#if R9
      float clNow = 1.0;
#endif
      if (dustM + gasM + frM > 0.0008) {
        lk = texture(uLit, uvw);   // r7: hoisted (same condition as any3): the striations fade into the shadowed bulk
        float lodR = log2(max(t, 0.5) * uPxA * uMed.z * uDetN);
        dp = ps * uMed.z + drift;
        float radK = 1.0;
        if (uStr.w > 0.5 && uStr.x < 0.001) {
          // ring (M57): the detail field streams radially from the star as seen along the ray (constant along
          // each ray, so strands stay whole through the optically thin shell); no extra fetch
          vec3 qr0 = ps - uStar; qr0 -= rd * dot(qr0, rd); float r0 = max(length(qr0), 0.3);
          float ka = uStr.z * uStr.w * uMed.z;
          dp = qr0 / r0 * (ka + 0.12 * r0) + drift;
          lodR += log2(max((ka / r0 + 0.12) / uMed.z, 1.0));
          radK = smoothstep(0.6 * uStr.w, 0.92 * uStr.w, r0);   // smooth OIII haze inside the ring (spokes there = fake god rays)
        }
        lod = clamp(lodR, 0.0, 5.0);
        vec4 n1 = textureLod(uDet, dp, lod);
        n1 = mix(vec4(0.5), n1, radK);
        n1r = n1.r;
        if (frM > 0.002) frM *= 0.35 + 1.9 * smoothstep(0.4, 0.8, textureLod(uDet, vec3(ps.x * 0.3, ps.y * 0.07 - uTime * 0.003, ps.z * 0.3) + 0.21, 0.0).b);   // steam: veil streaks rising off the ridge
        float e = (1.0 - mix(n1.r, n1.b, uRHz.z)) * uMed.y;   // ridged erosion: filaments, not billows
        dust = clamp((dustM - e) / max(1.0 - e, 0.06), 0.0, 1.0);
        ridge = n1.b;
#if DET2
        // wind-eroded striations (JWST Cosmic Cliffs / Pillars): a ridged octave stretched ~6x along the
        // cluster light (uStr.w = 0) or streaming radially from the star (uStr.w = ring radius), warped by
        // the first octave; only in the surface band, faded out with the footprint (LOD)
        bool rad = uStr.w > 0.5;
        vec3 qs = ps - uStar; float rq = length(qs);
        // ring: strands only in the shell + outer halo (radial streaks at the star read as fake god rays)
        float ringB = rad ? smoothstep(0.55 * uStr.w, 0.85 * uStr.w, rq) * (1.0 - smoothstep(1.3 * uStr.w, 1.6 * uStr.w, rq)) : 1.0;
        // r7: break the repeat. Low-frequency fields (macro 'chem' m.b, first octave n1.a) bend the streak
        // direction (a large-scale shift across the light), fold the ridged value in some regions (~2x the
        // streak count, no chirp) and gate the amplitude: strong on lit faces (cluster/star/fill), fading into
        // the shadowed bulk, where the extra fetch is skipped
        float lfA = m.b, lfB = n1.a;
        float amp = rad ? 1.0 : mix(1.0, smoothstep(0.06, 0.45, max(mix(lk.g, lk.r, uSW), lk.a * 0.8)) * (0.3 + 0.7 * smoothstep(0.32, 0.62, lfB + 0.35 * (lfA - 0.5))), uDep.w) * (1.0 - 0.6 * nearS);   // clean cavity walls
        if (uStr.x > 0.001 && ringB * amp > 0.1 && dust > 0.0 && dust < 0.95 && lodR < 2.5 && max(T.r, max(T.g, T.b)) > 0.25) {
          vec3 w3 = (n1.gba - 0.5) * 0.15;
          // ring: strands on the plane through the star facing the ray, so they stay whole through the optically
          // thin shell (3D strands average out along the ray); pillars: stretched along the cluster light
          vec3 qp = qs - rd * dot(qs, rd); float rp = length(qp);
          float sk = rad ? 2.2 / max(rp, 0.3) + 0.3 : 1.0;
          vec3 sx = normalize(cross(uLDir, vec3(0.0, 0.0, 1.0)) + 1e-4);
          vec3 bend = sx * ((lfA - 0.5) * 1.1 + (lfB - 0.5) * 0.4) * uDep.w;
          vec3 sp = rad ? qp / max(rp, 1e-3) * 2.2 + qp * 0.3 + w3 * 0.3 : ps - uLDir * (dot(ps, uLDir) * mix(0.9, 0.84 + 0.12 * lfA, uDep.w)) + w3 + bend;
          vec4 n2 = textureLod(uDet, sp * (uMed.z * uStr.z) + drift, max(lodR + log2(uStr.z * sk), 0.0));
          float fade = (1.0 - smoothstep(1.3, 2.5, lodR)) * ringB * amp;
          float band = (1.0 - 0.6 * smoothstep(0.5, 1.0, dust)) * fade;   // dense cores stay dark and whole
          float nb = n2.b;
          if (!rad) nb = mix(nb, 1.0 - abs(2.0 * nb - 1.0), smoothstep(0.42, 0.68, lfA + 0.4 * (lfB - 0.5)) * 0.5 * uDep.w);
          float sn = nb * nb * (3.0 - 2.0 * nb);
          float e2 = (1.0 - sn) * uStr.x * band;
          dust = clamp((dust - e2) / max(1.0 - e2, 0.06), 0.0, 1.0);
          ridge = mix(ridge, sn, uStr.y * fade);                          // skins + glow follow the striations
          strM = mix(1.0, 0.5 + 1.5 * sn * sn, uStr.y * fade);           // ~mean-preserving streaks on the visible layer
          if (rad) gasM *= mix(1.0, 0.15 + 1.9 * sn * sn, uStr.y * fade);      // ring: the emission shell streams too
        }
#endif
        dust *= uMed.x;
        gas = uGasP.x * (gasM * (1.0 - 0.6 * smoothstep(0.1, 0.5, dustM)) * (0.25 + 1.3 * n1.a) * mix(1.0, 0.3 + 1.4 * ridge * ridge, uGasP.z)
                         + uGasF.x * frM * (0.2 + 1.8 * ridge * ridge * n1.r));
      } else if (rs > uCoc.x * 2.2 && rs > 1.5) {
        dt *= 2.0;
      }
      dt = min(dt, max(0.28 / sigPrev, 0.018));
      dt = max(dt, (t1 - t) / (maxI - float(i)) * 0.85);   // never run out of steps before the far side
      // short tap toward the dominant light (cluster before ignition, star after): used for
      // the thin ionisation-front skins and for the fine self-shadow
      bool any3 = dustM + gasM + frM > 0.0008;
      float tapD = 0.0, tapM = 0.0;
      vec3 ls = -q / max(rs, 1e-4);
      if (any3) {
#if TAP
        if (dustM > 0.003) {
          vec3 ldir = normalize(mix(uLDir, ls, uSW));
          vec3 pt = ps + ldir * 0.16;
          tapD = texture(uVol, (pt - uBoxMin) * uBoxInv).r; tapD *= tapD * comp * cav;
          // macro-only tap further out: big faces toward the new star (no per-wisp skins)
          if (uLightP.x > 0.0) { tapM = texture(uVol, (ps + ls * 0.38 - uBoxMin) * uBoxInv).r; tapM *= tapM * comp * cav; }
#if TAPD
          float eT = (1.0 - textureLod(uDet, pt * uMed.z + drift, lod).r) * uMed.y;
          tapD = clamp((tapD - eT) / max(1.0 - eT, 0.06), 0.0, 1.0);
#endif
        }
#endif
        float surf = smoothstep(0.0, 0.08, dust) * (1.0 - smoothstep(0.0, 0.2, tapD)) + 0.25 * smoothstep(0.02, 0.3, frM) * uGasF.x;
        float fallS = uLightP.x / (1.0 + r2 / sq(uLightP.y));
        fallS *= (1.0 - smoothstep(uLightP.z - uLightP.w, uLightP.z, rs)) + uBand * exp(-sq((rs - uLightP.z) / (uLightP.w * 0.45)));
        float surfM = smoothstep(0.0, 0.12, dust) * (1.0 - smoothstep(0.05, 0.4, tapM));
#if R8
        // r8: skins thin to the star-facing crests (ridge^3, short tap clear); only the thin dust fringe emits (band-pass): magenta rims, not a fill
        float r3 = ridge * ridge * ridge;
        float skC = mix(0.2 + 1.9 * ridge * ridge, 0.04 + 2.2 * r3, uR8.x);
        float skS = mix(0.5 + 0.8 * ridge, (1.0 - smoothstep(0.0, 0.16, tapD)) * (1.0 - smoothstep(0.06, 0.28, dust) * uR8b.w) * (0.08 + 3.2 * r3), uR8.x);
#if R9
        // r9, ROOT CAUSE of the square cells: the band-pass made the skin a shell thinner than one march step, so it emitted
        // only where a jittered sample happened to land inside it (per-pixel fireflies) and the exact 3x3 box denoise turned
        // each firefly into a flat 3x3-texel square (~7 px after the upscale). Pre-integrate instead: the share of this step's
        // dust ramp (dPrev -> dust) inside the fringe, times the step-averaged star-tap clear. Every ray that crosses a front
        // gets its share (limb-brightened by the path length in the fringe) and none gets a spike.
        float clT = 1.0 - smoothstep(0.0, 0.16, tapD); clNow = clT;
        float dd9 = dust - dPrev;
        float fr9 = mix(smoothstep(0.0, 0.12, dust) * (1.0 - smoothstep(0.06, 0.28, dust) * uR8b.w), 1.2 * (abs(dd9) > 1e-3 ? (frI(dust) - frI(dPrev)) / dd9 : frB(dust)), uR9.x);
        float sk9 = mix(clT, 0.5 * (clT + clPrev), uR9.x) * fr9 * (1.0 - smoothstep(0.05, 0.4, tapM)) * (0.08 + 3.2 * r3) * uR9.y;
        vec3 Esk = uRim.x * lk.b * uLI * uCR * skC * surf + uRim.y * lk.r * lk.r * fallS * uCR2 * mix((0.5 + 0.8 * ridge) * surfM * (1.0 + 0.5 * nearS), sk9, uR8.x);
#else
        vec3 Esk = uRim.x * lk.b * uLI * uCR * skC * surf + uRim.y * lk.r * lk.r * fallS * uCR2 * skS * surfM * (1.0 + 0.5 * nearS * (1.0 - uR8.x));
#endif
#else
        vec3 Esk = uRim.x * lk.b * uLI * uCR * (0.2 + 1.9 * ridge * ridge) * surf + uRim.y * lk.r * lk.r * fallS * uCR2 * (0.5 + 0.8 * ridge) * surfM * (1.0 + 0.5 * nearS);
#endif
        col += T * Esk * strM * dt;
      }
      vec3 Sc = vec3(0.0), Sd2 = vec3(0.0);
      float dC = 0.0, dD = 0.0, dS = 0.0;
      if (uCoc.y > 0.0 && rs < uCoc.x * 2.2) dC = cocoon(q, rs, rd, Sc);
      if (uDisk.x > 0.0) dD = diskD(q, Sd2);
      if (uShellB.z > 0.0) dS = shellD(q, rs);
      sigPrev = max((dust + dC + dD) * uMed.w * 1.25, max(dustM * uMed.w * 2.0, 1e-3));
      if (dust + gas + dC + dD + dS > 1e-4) {
        if (!any3) lk = texture(uLit, uvw);
        vec2 lt = lk.rg * lk.rg;
        float Ts = lt.r, Tl = lt.g;
#if TAP
        // short-range self shadow toward the light: resolves rims the coarse light volume can't
        float sh = exp(-uMed.w * (tapD * uMed.x * 0.75 + dust * 0.25) * uGasF.w);
        Ts *= mix(1.0, sh, uSW); Tl *= mix(sh, 1.0, uSW);
#endif
        float front = 1.0 - smoothstep(uLightP.z - uLightP.w, uLightP.z, rs);
        float band = uBand * exp(-sq((rs - uLightP.z) / (uLightP.w * 0.45)));
        float fall = uLightP.x / (1.0 + r2 / sq(uLightP.y));
        float I = Ts * fall * (front + band);
        float cS = dot(rd, ls);
        float ph = 0.6 * hg(cS, 0.3) + 0.4 * hg(cS, -0.2);
        float Il = Tl * uLI * mix(0.35, 1.0, smoothstep(2.5, 6.0, t));
        float phl = 0.7 * hg(dot(rd, uLDir), 0.62) + 0.3;
        float heat = uHeat.w * (0.25 + 0.75 * Ts) / (1.0 + r2 / 0.8);
        // r7 depth: inside the dust and shadowed from the star by more dust (macro tap) = the bulk: no magenta glow,
        // dimmer diffuse light; only the ionised skins facing the star stay magenta
        float shield = smoothstep(0.05, 0.35, tapM);
#if R8
        // r8: dark rust/umber bodies (uAlb/uAlbL/uGain.y blended on the CPU) carved by the ridge field; the star lights the near walls rust (uR8c.x)
        float carve = mix(1.0, 0.18 + 1.7 * ridge * ridge, uR8.y);
        vec3 Sd = I * (uStarCol * mix(uAlb, uAlbC.rgb, nearS) * ph * uGain.z * (1.0 - uR8c.x * nearS) * carve + uCR * uGain.y * (1.0 - uDep.x * shield))
                + Il * uLCol * uAlbL * phl * carve
#else
        vec3 Sd = I * (uStarCol * mix(uAlb, uAlbC.rgb, nearS) * ph * uGain.z * (1.0 - 0.85 * nearS) + uCR * uGain.y * (1.0 - uDep.x * shield))
                + Il * uLCol * uAlbL * phl
#endif
                + (uAmb * (0.25 + 0.75 * Tl) + heat * uHeatA * 0.6 + uDustE * (0.4 + 0.6 * ridge) * (0.25 + 0.75 * Tl)
#if R8
                + uFill.x * lk.a * lk.a * mix(1.0, lk.a, uR8.y) * uFillC * (0.15 + 1.2 * ridge * ridge) * smoothstep(uFill.y, uFill.z, p.y)) * (1.0 - uDep.z * shield);
#else
                + uFill.x * lk.a * lk.a * uFillC * (0.15 + 1.2 * ridge * ridge) * smoothstep(uFill.y, uFill.z, p.y)) * (1.0 - uDep.z * shield);
#endif
        float ion = clamp(I * uGain.w / (0.12 + dust * 5.0 + gas * 1.5), 0.0, 1.0);
        vec3 ec = mix(uCS, uCH, smoothstep(0.0, 0.25, ion + 0.18 * (m.b - 0.5)));
        // r7: OIII teal fills the open cavity; at the dust (the ionisation fronts) the gas stays magenta
        ec = mix(ec, uCO, smoothstep(0.3, 0.9, ion) * (0.5 + 0.7 * m.b) * (1.0 - uDep.y * smoothstep(0.004, 0.08, dustM)));
        // ring (M57 layering by radius): teal-cyan inside, violet filament shell, crimson outer knots
        if (uStr.w > 0.5) { float rN = rs / uStr.w; ec = mix(mix(uCO, uCH, smoothstep(0.72, 0.95, rN)), uCS, smoothstep(1.06, 1.3, rN)); }
        vec3 gl = mix(uCG, uCH * 0.5 + uCG * 0.5, smoothstep(0.55, 0.9, m.b) * 0.6);
#if R8
        float frW = mix(0.3 + 1.2 * smoothstep(0.008, 0.1, dustM) * (1.0 - smoothstep(0.3, 0.8, dustM)), 0.08 + 1.0 * smoothstep(0.004, 0.05, dustM) * (1.0 - smoothstep(0.08, 0.3, dustM)), uR8b.y);   // r8: thinner gas fronts
        float gK = (1.0 - uDep.x * shield) * mix(1.0, frW, uDep.y * (1.0 - 0.75 * nearS)) * (1.0 - uR8b.z * smoothstep(0.02, 0.15, dustM));   // r8: no gas glow inside the dust bodies   // cavity gas near the star keeps glowing (teal)
#else
        float frW = 0.3 + 1.2 * smoothstep(0.008, 0.1, dustM) * (1.0 - smoothstep(0.3, 0.8, dustM));
        float gK = (1.0 - uDep.x * shield) * mix(1.0, frW, uDep.y * (1.0 - 0.75 * nearS));   // cavity gas near the star keeps glowing (teal)
#endif
        vec3 jg = gas * (I * ec * uGain.x * (0.6 + 0.8 * ridge) * gK + heat * uHeatA + Il * gl * uGasF.y * (0.5 + ridge));
        jg += uGasP.x * uGasF.x * frM * (0.2 + 1.8 * ridge * ridge * n1r) * I * ec * uGasP.w;
        jg += dS * uShellB.w * uShellCol * (0.3 + fall);
        vec3 sD = dust * uMed.w * uRedden;
        vec3 sC = (dC + dD) * uMed.w * uRedden;
        vec3 sig = sD + sC + (gas * uGasP.y + dS * 0.3) * uRedden + 1e-4;
        vec3 j = sD * Sd * strM + jg + dC * uMed.w * uRedden * (Sc + I * uStarCol * uAlb * ph * uGain.z * 2.0) + dD * uMed.w * uRedden * Sd2;
        vec3 Tst = exp(-sig * dt);
        col += T * j * (1.0 - Tst) / sig;
        T *= Tst;
#if R8
      }
      if (uR8.w > 0.0 && frM > 0.002 && max(T.r, max(T.g, T.b)) > 0.3) {
        // r8: teal-cyan / blue-white veils in front of the upper cliffs (baked veil field, no extra fetch; end of the step = low register pressure)
        float vy = smoothstep(uVeilY.x, uVeilY.y, p.y) * (1.0 - smoothstep(uVeilY.z, uVeilY.w, p.y));
        float vw = frM * vy * smoothstep(1.2, 2.6, rs) * (1.0 - smoothstep(0.0, 0.25, dustM));
        col += T * mix(uVeilC, vec3(0.5, 0.75, 1.0), smoothstep(0.5, 0.9, n1r)) * (uR8.w * vw * (0.25 + 1.5 * ridge * n1r)) * dt;
#endif
      }
#if R9
      dPrev = dust; clPrev = clNow;
#endif
      dtL = dt; t += dt;
    }
  }
  if (!jetDone) col += T * (jetGlow(ro, rd) + hazeGlow(ro, rd));
  float floorHit = (t1 > t0 && ro.y + rd.y * t1 < uBoxMin.y + 0.05) ? 1.0 : 0.0;
  col += T * mix(backdrop(rd), uDustE * 0.5, floorHit * uFloorF.x);
  col = min(col, vec3(4000.0));
#if LDR
  col = col / (1.0 + col);
#endif
  gl_FragColor = vec4(col, dot(T, vec3(0.3333)));
}`;

/* ---------------- 1x1: star visibility from the camera ---------------- */
export const VIS = /* glsl */`
precision highp float; precision highp sampler3D;
uniform vec3 uCamPos;
uniform vec4 uGasF; uniform float uVisCav;   // r7: star visibility keeps the r6 calibration unless k_vc
${COMMON}
${MEDIUM}
void main() {
  vec3 ro = uCamPos; vec3 dv = uStar - ro; float L = length(dv); vec3 rd = dv / L;
  vec3 od = vec3(0.0);
  float h = max(L - 0.03, 0.0) / 40.0;
  for (int i = 0; i < 40; i++) {
    vec3 p = ro + rd * (float(i) + 0.5) * h;
    vec3 q = p - uStar; float rs = length(q);
    vec3 uvw = (pullP(p) - uBoxMin) * uBoxInv;
    float d = 0.0;
    if (all(greaterThan(uvw, vec3(0.0))) && all(lessThan(uvw, vec3(1.0)))) {
      vec4 m = texture(uVol, uvw);
      float cav = uVisCav > 0.0 ? smoothstep(uVisCav * 0.5, uVisCav * 1.3, rs) : 1.0;
      d = m.r * m.r * uMed.x * 0.8 * cav * (1.0 + 1.6 * uPull.x * exp(-rs / uPull.z));
    }
    vec3 S;
    if (uCoc.y > 0.0) d += cocoon(q, rs, rd, S);
    if (uDisk.x > 0.0) d += diskD(q, S);
    if (uShellB.z > 0.0) d += shellD(q, rs) * 0.3;
    od += d * h * uMed.w * uRedden;
  }
  gl_FragColor = vec4(exp(-od), 1.0);
}`;
